import { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Card,
  Grid,
  Stack,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  Dialog,
  IconButton,
} from "@mui/material";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import CloseIcon from "@mui/icons-material/Close";
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";
import CustomTextField from "../../shared-components/form-controls/customTextField";
import dataConstant from "../../constants/dataConstants";
import CustomMultipleImageUpload from "../../shared-components/customMultipleImageUpload";
import urlConstant from "../../constants/urlConstants";
import { DocumentUpload, httpGet } from "../../services/commonService";
import CommonSnackBar from "../../shared-components/snackbar/commonSnackbar";
import SnackState from "../../shared-components/snackbar/snackState";
import {
  nameValidator,
  phoneNumberValidator,
} from "../../shared-components/validator/fieldValidator";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

function ManageAffiliateProjects() {
  const authenticationState = useContext(AuthenticationContext);
  const navigate = useNavigate();
  const [snack, closeSnack, showSnackbar] = SnackState();
  const [value, setValue] = useState(0);
  const location = useLocation();
  const { t } = useTranslation();
  //const businessType = location.state?.businessType || "";
  const [businessType, setBusinessType] = useState(
    location.state?.businessType || ""
  );
  const formTitle = `${t("LeadCollection.AddLead")} - ${businessType}`;
  const [state, setState] = useState({
    isDeletedPopupOpen: false,
    selectedDeletedImage: "",
    isDisplay: false,
    response: {},
    isSubmitDisabled: false,
    formTitle: "",
    projectList: [],
    isAddLeadPopupOpen: false,
    leadInfo: {
      firstName: "",
      lastName: "",
      emailAddress: "",
      phoneNumber: "",
      leadStatus: dataConstant.LeadCollections.LeadStatus.New,
    },
    uploadBills: [],
  });

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      leadInfo: {
        ...prevState.leadInfo,
        emailAddress: "",
        firstName: "",
        lastName: "",
        phoneNumber: "",
      },
      isDisplay: true,
      formTitle: formTitle.toString(),
    }));
  }, [location]);

  useEffect(() => {
    if (businessType === "") {
      httpGet(
        urlConstant.Common.GetBusinessTypeDropDownList,
        true,
        false,
        authenticationState.accessToken,
        authenticationState.impersonateEmail
      )
        .then((response) => {
          setState((prevState) => ({
            ...prevState,
            isAddLeadPopupOpen: true,
            projectList: response.Value.Data,
          }));
        })
        .catch((err) => {
          console.error(err);
        });
    }
  }, [businessType]);

  const methods = useForm({
    mode: "all",
    defaultValues: {
      firstName: "",
      lastName: "",
      emailAddress: "",
      phoneNumber: "",
    },
  });

  const { handleSubmit } = methods;

  const handleImageChange = (files) => {
    if (files?.length > 0) {
      const newFiles = files.map((file, index) => ({
        id: Date.now() + index,
        images: [URL.createObjectURL(file)],
        originalFileName: file.name,
        file: file,
        fileExtension: file.name?.split('.').pop().split('?')[0].toLowerCase()
      }));
      setState((prev) => ({
        ...prev,
        imageDisplay: true,
        uploadBills: [...(prev.uploadBills || []), ...newFiles],
      }));
    }
  };

  const handleDeleteImage = (id) => {
    setState((prevState) => ({
      ...prevState,
      isDeletedPopupOpen: true,
      selectedDeletedImage: id,
    }));
  };

  const handleYesDeletePopupModal = () => {
    const updatedUploadBills = state.uploadBills.filter(
      (data) => data.id !== state.selectedDeletedImage
    );

    setState((prevState) => ({
      ...prevState,
      uploadBills: updatedUploadBills,
      isDeletedPopupOpen: false,
      selectedDeletedImage: "",
    }));
  };

  const handleNoDeletePopupModal = () => {
    setState((prevState) => ({
      ...prevState,
      isDeletedPopupOpen: false,
      selectedDeletedImage: "",
    }));
  };

  const firstNameLabelKey = (businessType === dataConstant.ReinH2O.label || businessType === dataConstant.HomeService.label) ? t('LeadCollection.ReinH2O.CustomerFirstName') : t('LeadCollection.FirstNameLable')
  const lastNameLabelKey = (businessType === dataConstant.ReinH2O.label || businessType === dataConstant.HomeService.label) ? t('LeadCollection.ReinH2O.CustomerLastName') : t('LeadCollection.LastNameLable')
  const formConfig = {
    firstName: {
      name: "firstName",
      type: "text",
      required: true,
      label: t(firstNameLabelKey),
      maxLength: dataConstant.MinMaxLength.CharLimit50,
      validation: {
        validate: nameValidator(firstNameLabelKey),
      },
      tabIndex: 1,
    },
    lastName: {
      name: "lastName",
      type: "text",
      required: true,
      label: t(lastNameLabelKey),
      maxLength: dataConstant.MinMaxLength.CharLimit50,
      validation: {
        validate: nameValidator(lastNameLabelKey),
      },
      tabIndex: 2,
    },
    emailAddress: {
      name: "emailAddress",
      type: "email",
      required: true,
      label: t((businessType === dataConstant.ReinH2O.label || businessType === dataConstant.HomeService.label) ? t('LeadCollection.ReinH2O.CustomerEmailAddress') : t('LeadCollection.EmailLable')),
      maxLength: dataConstant.MinMaxLength.CharLimit50,
      tabIndex: 3,
    },
    phoneNumber: {
      name: "phoneNumber",
      type: "text",
      required: true,
      label: t((businessType === dataConstant.ReinH2O.label || businessType === dataConstant.HomeService.label) ? t('LeadCollection.ReinH2O.CustomerPhoneNumber') : t('LeadCollection.PhoneLable')),
      validation: {
        validate: phoneNumberValidator(
          businessType === dataConstant.ReinH2O.label
            ? "LeadCollection.ReinH2O.CustomerPhoneNumber"
            : "LeadCollection.PhoneLable"
        ),
      },
      maxLength: dataConstant.MinMaxLength.CharLimit10,
      tabIndex: 4,
    },
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const onSubmit = async (data) => {
    const personalInfoFields = [
      "firstName",
      "lastName",
      "emailAddress",
      "phoneNumber",
    ];
    const isValid = await methods.trigger(personalInfoFields);
    if (!isValid) return;
    const isSolar = businessType === dataConstant.Solar.label;
    const hasSelectedFile = state.uploadBills.length > 0;
    if (isSolar && !hasSelectedFile) {
      showSnackbar(t("LeadCollection.UtilityBillRequired"), "warning");
      return;
    }
    setState((prevState) => ({
      ...prevState,
      isSubmitDisabled: true,
    }));
    createLead(data);
  };

  const createLead = (event) => {
    const model = {
      FirstName: event.firstName,
      LastName: event.lastName,
      Phone: event.phoneNumber,
      PublicEmail: event.emailAddress.toLowerCase(),
      LeadStatus: dataConstant.LeadCollections.LeadStatus.New,
      BusinessTypeID: businessType === dataConstant.ReinH2O.label ? dataConstant.ReinH2O.value : businessType === dataConstant.HomeService.label ? dataConstant.HomeService.value :dataConstant.Solar.value
    };
    const createURL = urlConstant.LeadCollections.CraeteLeadCollection;
    const formData = new FormData();
    for (let i = 0; i < state.uploadBills.length; i++) {
      formData.append("file", state.uploadBills[i].file);
    }
    formData.append("model", JSON.stringify(model));
    DocumentUpload(
      createURL,
      formData,
      true,
      true,
      authenticationState.accessToken,
      authenticationState.impersonateEmail
    )
      .then((response) => {
        if (response.Status === dataConstant.APIResponseStatus.Success) {
          showSnackbar(t("LeadCollection.LeadSaveSuccess"), "success");
          setTimeout(() => {
            navigate("/affiliateProjects");
          }, 1000);
        } else {
          showSnackbar(t("LeadCollection.LeadSaveFailed"), "error");
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleImageUploadError = (errorData) => {
    switch (errorData.type) {
      case "maxImages":
        showSnackbar(errorData.message, "warning");
        break;
      case "fileSize":
        showSnackbar(errorData.message, "warning");
        break;
      case "fileType":
        showSnackbar(errorData.message, "warning");
        break;
      case "fileValidation":
        showSnackbar(errorData.message, "warning");
        break;
      case "processing":
        showSnackbar(errorData.message, "warning");
        break;
      default:
        console.log(errorData.message);
    }
  };

  return (
    <>
      <Box flexDirection={"column"} gap={2} display={"flex"}>
        <Typography
          color="secondary.light"
          display={{ xs: "flex", sm: "none" }}
          variant="h4"
          fontSize={"24px"}
          fontWeight={600}
        >
          {t("ProjectsPage.AffiliateMenuName")}
        </Typography>
        <Box width={"100%"}>
          <Card
            sx={(theme) => ({
              p: {
                xs: "20px",
                sm: "25px 30px",
              },
              background: theme.palette.background.cardGradient,
              border: "1px solid var(--headerbg)",
              borderRadius: "20px",
              height: "100%",
            })}
          >
            <Stack gap={"20px"}>
              <Typography color="secondary.light" variant="h4" fontWeight={600}>
                {formTitle}
              </Typography>
              {state.isDisplay ? (
                <Stack className="tabs-common">
                  <Box sx={{ px: 2 }}>
                    <Tabs
                      value={value}
                      onChange={handleChange}
                      aria-label="basic tabs example"
                    >
                      <Tab
                        label={(businessType === dataConstant.ReinH2O.label || businessType === dataConstant.HomeService.label) ? t('LeadCollection.ReinH2O.CustomerContactInfo') : t('LeadCollection.ContactInfo')}
                        {...a11yProps(0)}
                        sx={{ textTransform: "none" }}
                      />
                    </Tabs>
                  </Box>
                  <Stack>
                    <form onSubmit={handleSubmit(onSubmit)}>
                      <CustomTabPanel value={value} index={0}>
                        <Grid
                          container
                          spacing={3}
                          sx={{
                            flexGrow: 1,
                            width: "100%",
                          }}
                        >
                          <Grid size={{ xs: 12, sm: 12 }} display={businessType === dataConstant.ReinH2O.label || businessType === dataConstant.Solar.label ? "" : "none"}>
                            <Card
                              sx={(theme) => ({
                                p: {
                                  xs: "20px",
                                  sm: "25px 30px",
                                },
                                background: theme.palette.common.headerbg,
                                borderRadius: "20px",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                width: "100%",
                                gap: 1,
                              })}
                            >
                              <CustomMultipleImageUpload
                                label={
                                  businessType === dataConstant.ReinH2O.label  || businessType === dataConstant.HomeService.label  ? (
                                    t("LeadCollection.ReinH2O.UploadImages")
                                  ) : (
                                    <>
                                      {t("LeadCollection.UploadLable")}{" "}
                                      <span style={{ color: "red" }}>*</span>
                                    </>
                                  )
                                }
                                supportedFormats={["jpg", "jpeg", "png", "pdf"]}
                                isViewOnly={false}
                                images={state.uploadBills}
                                maxSize={
                                  dataConstant.LeadCollections.MAX_FILE_SIZE_MB
                                }
                                maxImages={
                                  dataConstant.LeadCollections.MAX_LENGTH
                                }
                                onImageChange={handleImageChange}
                                deleteImage={handleDeleteImage}
                                onError={handleImageUploadError}
                                showInternalErrors={false}
                              />
                            </Card>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <CustomTextField
                              methods={methods}
                              properties={formConfig.firstName}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <CustomTextField
                              methods={methods}
                              properties={formConfig.lastName}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <CustomTextField
                              methods={methods}
                              properties={formConfig.emailAddress}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <CustomTextField
                              methods={methods}
                              properties={formConfig.phoneNumber}
                            />
                          </Grid>
                          <Stack
                            direction="row"
                            width={"100%"}
                            gap={"20px"}
                            alignItems={"center"}
                            justifyContent={"center"}
                            flexWrap="wrap"
                          >
                            <Button
                              sx={{ px: 3, width: { xs: "100%", sm: "auto" } }}
                              variant="contained"
                              type="button"
                              color="secondary"
                              onClick={() => navigate(-1)}
                            >
                              {t("LeadCollection.Cancel")}
                            </Button>
                            <Button
                              sx={{ px: 3, width: { xs: "100%", sm: "auto" } }}
                              variant="contained"
                              type="button"
                              onClick={handleSubmit(onSubmit)}
                              disabled={state.isSubmitDisabled}
                            >
                              {t("Common.Save")}
                            </Button>
                          </Stack>
                        </Grid>
                      </CustomTabPanel>
                    </form>
                  </Stack>
                </Stack>
              ) : null}
            </Stack>
          </Card>
        </Box>
      </Box>

      {/* Add Lead Popup - Choose Business Type */}
      <Dialog
        open={state.isAddLeadPopupOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick" && reason === "escapeKeyDown") {
            return;
          }
        }}
        aria-labelledby="customized-dialog-title"
        sx={{ borderRadius: "20px", background: "transparent" }}
      >
        <Stack
          sx={(theme) => ({
            background: theme.palette.common.black,
            borderTopLeftRadius: "20px",
            borderTopRightRadius: "20px",
          })}
        >
          <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
            {t("LeadCollection.AddLead")}
          </DialogTitle>
        </Stack>
        <DialogContent
          sx={(theme) => ({
            background: theme.palette.background.cardGradient,
            border: 0,
            borderBottomLeftRadius: "20px",
            borderBottomRightRadius: "20px",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            alignItems: "center",
          })}
          dividers
        >
          <Button
            variant="contained"
            sx={{ px: 0.8, py: 0.5, width: "auto", backgroundColor: "#FF6C6C" }}
            color="primary"
            disableElevation
          >
            <img src="/icons/user-header.svg" alt="user" />
          </Button>
          <Stack direction={"column"} alignItems={"center"}>
            <Typography color="secondary.light" variant="body1">
              {t("LeadCollection.LeadFor")}
            </Typography>
          </Stack>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            flexWrap={"wrap"}
            gap={2}
            width={"100%"}
            justifyContent={"center"}
            borderTop={"1px solid var(--headerbg)"}
            paddingTop={"20px"}
          >
            {state.projectList.map((item, index) => {
              return (
                <Button
                  variant="contained"
                  sx={{ px: 3, width: { xs: "100%", sm: "auto" } }}
                  key={index}
                  onClick={() => {
                    setBusinessType(item.label);
                    setState((prevState) => ({
                      ...prevState,
                      isAddLeadPopupOpen: false,
                      formTitle: `${t("LeadCollection.AddLead")}  - ${
                        item.label
                      }`,
                    }));
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Stack>
        </DialogContent>
      </Dialog>

      {state.isDeletedPopupOpen && (
        <Dialog
          onClose={handleNoDeletePopupModal}
          aria-labelledby="customized-dialog-title"
          open={state.isDeletedPopupOpen}
          sx={{ borderRadius: "20px", background: "transparent" }}
        >
          <Stack
            sx={(theme) => ({
              background: theme.palette.common.black,
              borderTopLeftRadius: "20px",
              borderTopRightRadius: "20px",
            })}
          >
            <DialogTitle id="delete-confirm-dialog-title">
              {t("LeadCollection.UploadedBillsLable")}
            </DialogTitle>
            <IconButton
              aria-label="close"
              onClick={handleNoDeletePopupModal}
              sx={(theme) => ({
                position: "absolute",
                right: 8,
                top: 8,
                color: theme.palette.grey[500],
                p: 1,
                borderRadius: "8px",
              })}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
          <DialogContent
            sx={(theme) => ({
              background: theme.palette.background.cardGradient,
              border: 0,
              borderBottomLeftRadius: "20px",
              borderBottomRightRadius: "20px",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              alignItems: "center",
            })}
            dividers
          >
            <Stack textAlign={"center"}>
              <Typography
                color="common.light"
                sx={{}}
                variant="h4"
                fontWeight={600}
              >
                <Typography>{t("LeadCollection.DeletePopupText")}</Typography>
              </Typography>
            </Stack>
            <DialogActions>
              <Button
                variant="contained"
                color="primary"
                sx={{ px: 3 }}
                fullWidth
                onClick={handleYesDeletePopupModal}
              >
                {t("LeadCollection.Yes")}
              </Button>
            </DialogActions>
          </DialogContent>
        </Dialog>
      )}
      <CommonSnackBar snackObj={snack} closeSnack={closeSnack} buttonProp />
    </>
  );
}
export default ManageAffiliateProjects;